<template>
<div class="tt-root" :style="themePageStyle">
  <nav class="tt-topbar"><div class="container d-flex align-items-center py-2">
    <a href="javascript:void(0)" @click="$router.go(-1)" class="tt-back"><i class="mdi mdi-arrow-left"></i></a>
    <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
    <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Trip {{ booking.booking_ref }}</span>
  </div></nav>

  <div class="container tt-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!booking.id" class="text-center py-5">
      <i class="mdi mdi-truck-remove" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Trip Not Found</h5>
    </div>

    <div v-else class="row g-4">
      <!-- Left column -->
      <div class="col-lg-8">
        <!-- Status header -->
        <div class="tt-card mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <span class="tt-status" :class="'st-'+booking.status">{{ booking.status.replace(/_/g,' ') }}</span>
              <span class="ms-2" style="color:var(--fm-s,#6B6356);font-size:11px;">{{ booking.booking_ref }}</span>
            </div>
            <div v-if="booking.scheduled_pickup_date" style="color:var(--fm-s,#6B6356);font-size:12px;">
              <i class="mdi mdi-calendar me-1"></i>{{ booking.scheduled_pickup_date }}
              <span v-if="booking.scheduled_pickup_time" class="ms-1">· {{ booking.scheduled_pickup_time }}</span>
            </div>
          </div>

          <!-- Status progression -->
          <div class="tt-timeline">
            <div v-for="(step) in statusSteps" :key="step.k" class="tt-timeline-step" :class="{done: stepDone(step.k), current: step.k===booking.status}">
              <div class="tt-timeline-dot"></div>
              <div class="tt-timeline-label">{{ step.label }}</div>
            </div>
          </div>
        </div>

        <!-- Route -->
        <div class="tt-card mb-3">
          <h6 class="tt-section-title">Route</h6>
          <div class="d-flex align-items-start gap-3 mb-3">
            <div class="tt-route-dot" style="background:#4FC3F7;"></div>
            <div class="flex-fill">
              <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Pickup</div>
              <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ booking.pickup_address }}</div>
              <div v-if="booking.pickup_contact_name" style="color:var(--fm-m,#A09888);font-size:11px;margin-top:2px;">
                <i class="mdi mdi-account me-1"></i>{{ booking.pickup_contact_name }}
                <span v-if="booking.pickup_contact_phone" class="ms-2"><i class="mdi mdi-phone me-1"></i>{{ booking.pickup_contact_phone }}</span>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-start gap-3">
            <div class="tt-route-dot" style="background:#50C878;"></div>
            <div class="flex-fill">
              <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Delivery</div>
              <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ booking.delivery_address }}</div>
              <div v-if="booking.delivery_contact_name" style="color:var(--fm-m,#A09888);font-size:11px;margin-top:2px;">
                <i class="mdi mdi-account me-1"></i>{{ booking.delivery_contact_name }}
                <span v-if="booking.delivery_contact_phone" class="ms-2"><i class="mdi mdi-phone me-1"></i>{{ booking.delivery_contact_phone }}</span>
              </div>
            </div>
          </div>
          <div v-if="booking.estimated_distance_km || booking.estimated_duration_minutes" class="d-flex gap-3 mt-3 pt-2" style="border-top:1px solid var(--fm-br,#1E1E1E);font-size:11px;color:var(--fm-s,#6B6356);">
            <span v-if="booking.estimated_distance_km"><i class="mdi mdi-map-marker-distance me-1"></i>{{ Number(booking.estimated_distance_km).toFixed(1) }} km</span>
            <span v-if="booking.estimated_duration_minutes"><i class="mdi mdi-clock me-1"></i>~{{ Math.round(booking.estimated_duration_minutes/60) }}h {{ booking.estimated_duration_minutes%60 }}m</span>
          </div>
        </div>

        <!-- Cargo -->
        <div class="tt-card mb-3">
          <h6 class="tt-section-title">Cargo</h6>
          <div class="d-flex gap-3" style="flex-wrap:wrap;">
            <div><span style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Count</span><div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ booking.animal_count }} animals</div></div>
            <div v-if="booking.animal_type_description"><span style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Type</span><div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ booking.animal_type_description }}</div></div>
            <div v-if="booking.estimated_total_weight_kg"><span style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Weight</span><div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ booking.estimated_total_weight_kg }} kg</div></div>
          </div>
          <div v-if="booking.special_instructions" class="mt-3 pt-2" style="border-top:1px solid var(--fm-br,#1E1E1E);font-size:12px;color:var(--fm-m,#A09888);">
            <strong style="color:#BFA164;">Special instructions:</strong> {{ booking.special_instructions }}
          </div>
        </div>

        <!-- Pickup record — what the transporter actually loaded, visible to both parties -->
        <div v-if="booking.pickup_record || pickupPhotoList.length" class="tt-card mb-3">
          <h6 class="tt-section-title"><i class="mdi mdi-clipboard-check-outline me-1"></i>Pickup Record</h6>
          <div v-if="booking.pickup_record" style="color:var(--fm-h,#F5F0E8);font-size:13px;white-space:pre-wrap;">{{ booking.pickup_record }}</div>
          <div v-if="pickupPhotoList.length" class="d-flex gap-2 mt-2" style="flex-wrap:wrap;">
            <a v-for="(ph,i) in pickupPhotoList" :key="i" :href="pu(ph)" target="_blank">
              <img :src="pu(ph)" style="width:84px;height:84px;object-fit:cover;border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);" />
            </a>
          </div>
          <div v-if="booking.actual_pickup_at" style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:8px;">Picked up {{ formatTime(booking.actual_pickup_at) }}</div>
        </div>

        <!-- Transporter actions -->
        <div v-if="isTransporter" class="tt-card mb-3">
          <h6 class="tt-section-title">Transporter Actions</h6>
          <div v-if="booking.status==='requested'" class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-success flex-fill" @click="updateStatus('confirmed')"><i class="mdi mdi-check me-1"></i>Accept Trip</button>
            <button class="btn btn-sm btn-outline-danger flex-fill" @click="cancelModal.show=true"><i class="mdi mdi-close me-1"></i>Decline</button>
          </div>
          <div v-else-if="booking.status==='confirmed'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="updateStatus('en_route_to_pickup')"><i class="mdi mdi-truck-fast me-1"></i>Start trip — en route to pickup</button>
          </div>
          <div v-else-if="booking.status==='en_route_to_pickup'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="updateStatusWithGps('at_pickup')"><i class="mdi mdi-map-marker me-1"></i>Arrived at pickup</button>
          </div>
          <div v-else-if="booking.status==='at_pickup'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="updateStatus('loading')"><i class="mdi mdi-package-up me-1"></i>Start loading</button>
          </div>
          <!-- Issue 22: departure from pickup requires a record of what was loaded.
               The linked marketplace order then waits for the seller to approve the handover. -->
          <div v-else-if="booking.status==='loading'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="pickupModal.show=true"><i class="mdi mdi-truck-delivery me-1"></i>In transit to delivery</button>
          </div>
          <div v-else-if="booking.status==='in_transit'" class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-warning" @click="updateStatusWithGps('rest_stop')"><i class="mdi mdi-pause-circle me-1"></i>Rest stop</button>
            <button class="btn btn-sm btn-warning flex-fill" @click="updateStatusWithGps('at_delivery')"><i class="mdi mdi-map-marker-check me-1"></i>Arrived at delivery</button>
          </div>
          <div v-else-if="booking.status==='rest_stop'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="updateStatusWithGps('in_transit')"><i class="mdi mdi-truck-delivery me-1"></i>Resume trip</button>
          </div>
          <div v-else-if="booking.status==='at_delivery'" class="d-flex gap-2">
            <button class="btn btn-sm btn-warning w-100" @click="updateStatus('unloading')"><i class="mdi mdi-package-down me-1"></i>Start unloading</button>
          </div>
          <div v-else-if="booking.status==='unloading'" class="d-flex gap-2">
            <button class="btn btn-sm btn-success w-100" @click="updateStatus('completed')"><i class="mdi mdi-check-all me-1"></i>Mark Trip Completed</button>
          </div>
          <div v-else style="color:var(--fm-s,#6B6356);font-size:12px;">No further actions available in this state.</div>
        </div>
      </div>

      <!-- Right column -->
      <div class="col-lg-4">
        <div class="tt-card mb-3" style="position:sticky;top:80px;">
          <h6 class="tt-section-title">Payment</h6>
          <div class="d-flex justify-content-between mb-1" v-if="booking.quoted_price">
            <span style="color:var(--fm-s,#6B6356);font-size:12px;">Quoted</span>
            <span style="color:var(--fm-h,#F5F0E8);font-size:13px;">KES {{ fNum(booking.quoted_price) }}</span>
          </div>
          <div class="d-flex justify-content-between pt-2 mt-2" v-if="booking.final_price" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <span style="color:var(--fm-h,#F5F0E8);font-weight:700;">Final</span>
            <span style="color:#BFA164;font-weight:700;font-size:16px;">KES {{ fNum(booking.final_price) }}</span>
          </div>
          <div class="mt-2"><span class="tt-status" :class="'pst-'+booking.payment_status">{{ booking.payment_status }}</span></div>

          <div class="mt-4 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <h6 class="tt-section-title">Parties</h6>
            <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Requester</div>
            <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ booking.requester_name || '—' }}</div>
            <div v-if="booking.transporter_name" class="mt-3">
              <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Transporter</div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ booking.transporter_name }}</div>
              <div v-if="booking.transporter_phone" style="color:var(--fm-m,#A09888);font-size:11px;"><i class="mdi mdi-phone me-1"></i>{{ booking.transporter_phone }}</div>
            </div>
          </div>

          <div v-if="booking.current_latitude && booking.current_longitude && isInTransit" class="mt-4 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <h6 class="tt-section-title">Live Location</h6>
            <div style="color:#50C878;font-size:12px;"><i class="mdi mdi-circle-medium"></i> {{ Number(booking.current_latitude).toFixed(4) }}, {{ Number(booking.current_longitude).toFixed(4) }}</div>
            <div v-if="booking.last_location_update" style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:2px;">Updated {{ formatTime(booking.last_location_update) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Issue 22: pickup record modal — required before the trip can go In Transit -->
  <div v-if="pickupModal.show" style="position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1050;display:flex;align-items:center;justify-content:center;padding:16px;">
    <div class="tt-card" style="max-width:460px;width:100%;">
      <h6 class="tt-section-title"><i class="mdi mdi-clipboard-check-outline me-1" style="color:#BFA164;"></i>Record what you picked up</h6>
      <p style="font-size:12px;color:var(--fm-m,#A09888);margin-bottom:10px;">Describe the animals you loaded (count, type, tags, condition). The seller will confirm the handover against this record before the order moves to In Transit.</p>
      <textarea v-model="pickupModal.record" class="form-control form-control-sm mb-2" rows="3" placeholder="e.g. 2 Boran heifers, ear tags KE-0142 / KE-0143, both healthy and loaded calm" style="font-size:13px;"></textarea>
      <label style="font-size:11px;color:var(--fm-s,#6B6356);display:block;margin-bottom:4px;">Photos of the loaded animals (optional)</label>
      <input type="file" accept="image/*" multiple class="form-control form-control-sm mb-2" @change="pickupFilesChanged" />
      <div v-if="pickupModal.files.length" style="font-size:11px;color:#50C878;margin-bottom:8px;"><i class="mdi mdi-image-check me-1"></i>{{ pickupModal.files.length }} photo(s) attached</div>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary flex-fill" @click="pickupModal.show=false" :disabled="pickupModal.busy">Back</button>
        <button class="btn btn-sm btn-warning flex-fill" @click="submitPickup()" :disabled="pickupModal.busy || !pickupModal.record.trim()">{{ pickupModal.busy ? 'Saving...' : 'Confirm — In Transit' }}</button>
      </div>
    </div>
  </div>

  <!-- Admin-portal #18: transporter must give a reason when declining/cancelling -->
  <div v-if="cancelModal.show" style="position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1050;display:flex;align-items:center;justify-content:center;padding:16px;">
    <div class="tt-card" style="max-width:420px;width:100%;">
      <h6 class="tt-section-title"><i class="mdi mdi-close-circle-outline me-1" style="color:#FF6B6B;"></i>Decline this trip?</h6>
      <p style="font-size:12px;color:var(--fm-m,#A09888);margin-bottom:10px;">Please tell the requester why you are declining, so they can plan accordingly.</p>
      <textarea v-model="cancelModal.reason" class="form-control form-control-sm mb-3" rows="3" placeholder="Reason for declining (e.g. vehicle unavailable, route conflict...)" style="font-size:13px;"></textarea>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary flex-fill" @click="cancelModal.show=false" :disabled="cancelModal.busy">Keep trip</button>
        <button class="btn btn-sm btn-danger flex-fill" @click="confirmCancel()" :disabled="cancelModal.busy || !cancelModal.reason.trim()">{{ cancelModal.busy ? 'Declining...' : 'Decline trip' }}</button>
      </div>
    </div>
  </div>

  <transition name="tt-toast-fade">
    <div v-if="toast.show" class="tt-toast" :class="'toast-'+toast.type">
      <i class="mdi" :class="toast.type==='success' ? 'mdi-check-circle' : 'mdi-alert-circle'"></i>
      <span>{{ toast.msg }}</span>
    </div>
  </transition>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .tt-root{background:#F7F5F2!important}
html[data-theme="light"] .tt-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .tt-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.tt-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh}
.tt-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.tt-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:12px;text-decoration:none}
.tt-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.tt-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px}
.tt-section-title{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px}
.tt-status{font-size:10px;font-weight:600;padding:3px 10px;border-radius:8px;text-transform:capitalize}
.st-requested,.st-quoted{background:rgba(255,152,0,.1);color:#FF9800}
.st-confirmed{background:rgba(79,195,247,.1);color:#4FC3F7}
.st-en_route_to_pickup,.st-at_pickup,.st-loading,.st-in_transit,.st-rest_stop,.st-at_delivery,.st-unloading{background:rgba(191,161,100,.15);color:#BFA164}
.st-completed{background:rgba(80,200,120,.1);color:#50C878}
.st-cancelled{background:rgba(255,107,107,.1);color:#FF6B6B}
.pst-pending{background:rgba(255,152,0,.1);color:#FF9800}
.pst-paid{background:rgba(80,200,120,.1);color:#50C878}
.pst-refunded{background:rgba(255,107,107,.1);color:#FF6B6B}
.tt-timeline{display:flex;align-items:flex-start;justify-content:space-between;margin-top:16px;position:relative}
.tt-timeline::before{content:'';position:absolute;top:8px;left:10px;right:10px;height:2px;background:var(--fm-br,#1E1E1E);z-index:0}
.tt-timeline-step{flex:1;display:flex;flex-direction:column;align-items:center;position:relative;z-index:1;min-width:0}
.tt-timeline-dot{width:18px;height:18px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);border:2px solid var(--fm-bg1,#141414);transition:all .2s}
.tt-timeline-step.done .tt-timeline-dot{background:#50C878}
.tt-timeline-step.current .tt-timeline-dot{background:#BFA164;box-shadow:0 0 0 4px rgba(191,161,100,.2)}
.tt-timeline-label{font-size:9px;color:var(--fm-s,#6B6356);margin-top:6px;text-align:center;text-transform:capitalize;line-height:1.2}
.tt-timeline-step.done .tt-timeline-label,.tt-timeline-step.current .tt-timeline-label{color:var(--fm-h,#F5F0E8);font-weight:600}
.tt-route-dot{width:12px;height:12px;border-radius:50%;margin-top:4px;flex-shrink:0}

/* Toast */
.tt-toast{position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:900;display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:10px;font-size:13px;font-weight:600;box-shadow:0 10px 30px rgba(0,0,0,.4)}
.tt-toast .mdi{font-size:20px}
.tt-toast.toast-success{background:#1E3A2B;border:1px solid #50C878;color:#9FE8B5}
.tt-toast.toast-error{background:#3A1E1E;border:1px solid #FF6B6B;color:#FFB5B5}
.tt-toast-fade-enter-active,.tt-toast-fade-leave-active{transition:all .25s ease}
.tt-toast-fade-enter-from,.tt-toast-fade-leave-to{opacity:0;transform:translate(-50%,-12px)}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .tt-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .tt-card { padding: 12px !important; border-radius: 10px !important; }
  .tt-toast { font-size: 12px; padding: 8px 14px; top: calc(14px + env(safe-area-inset-top, 0px)); }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
export default {
  name: "TripDetail",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,
    booking:{},
    cancelModal:{show:false,reason:'',busy:false},
    pickupModal:{show:false,record:'',files:[],busy:false},
    userId:sessionStorage.getItem('userId')||'',
    statusSteps:[
      {k:'requested',label:'Requested'},
      {k:'confirmed',label:'Confirmed'},
      {k:'en_route_to_pickup',label:'En route'},
      {k:'at_pickup',label:'At pickup'},
      {k:'in_transit',label:'In transit'},
      {k:'at_delivery',label:'At delivery'},
      {k:'completed',label:'Completed'}
    ],
    toast:{show:false,msg:'',type:'success'},toastTimer:null
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    isTransporter(){return this.booking.transporter_user_id===this.userId},
    pickupPhotoList(){try{var a=JSON.parse(this.booking.pickup_photos||'[]');return Array.isArray(a)?a:[]}catch(e){return[]}},
    isInTransit(){return ['en_route_to_pickup','at_pickup','loading','in_transit','rest_stop','at_delivery','unloading'].indexOf(this.booking.status)>-1}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadBooking();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();if(this.toastTimer)clearTimeout(this.toastTimer);},
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    fNum(n){return Number(n||0).toLocaleString()},
    formatTime(d){if(!d)return'';var dt=new Date(d);return dt.toLocaleDateString('en-KE',{day:'numeric',month:'short'})+' '+dt.toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'})},
    showToast(msg,type){if(this.toastTimer)clearTimeout(this.toastTimer);this.toast={show:true,msg:msg,type:type||'success'};var self=this;this.toastTimer=setTimeout(function(){self.toast.show=false;},3000);},
    stepDone(key){
      var order=['requested','confirmed','en_route_to_pickup','at_pickup','loading','in_transit','rest_stop','at_delivery','unloading','completed'];
      var thisIdx=order.indexOf(key);
      var currentIdx=order.indexOf(this.booking.status);
      if(this.booking.status==='cancelled') return false;
      if(this.booking.status==='completed') return thisIdx<=currentIdx;
      return thisIdx<currentIdx;
    },
    async loadBooking(){
      this.loading=true;
      var id=this.$route.params.id;
      try{
        var r=await executeJson({function:'getTransportBooking',bookingId:id},flocker);
        if(r.data&&r.data.success&&r.data.data){this.booking=r.data.data.booking||{};}
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async updateStatus(status,cancellationReason){
      try{
        var actorUserId=sessionStorage.getItem('userId')||'';
        var payload={function:'updateTransportStatus',bookingId:this.booking.id,status:status,actorUserId:actorUserId};
        if(cancellationReason)payload.cancellationReason=cancellationReason;
        var r=await executeJson(payload,flocker);
        if(r.data&&r.data.success){await this.loadBooking();this.showToast('Status updated','success');return true;}
        else{this.showToast((r.data&&r.data.message)||'Update failed','error');return false;}
      }catch(e){this.showToast('Network error','error');return false;}
    },
    pu(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},
    pickupFilesChanged(e){this.pickupModal.files=Array.prototype.slice.call(e.target.files||[])},
    async submitPickup(){
      if(!this.pickupModal.record.trim())return;
      this.pickupModal.busy=true;
      var f=new FormData();
      f.append('function','updateTransportStatus');
      f.append('bookingId',this.booking.id);
      f.append('status','in_transit');
      f.append('actorUserId',sessionStorage.getItem('userId')||'');
      f.append('pickupRecord',this.pickupModal.record.trim());
      this.pickupModal.files.forEach(function(file,i){f.append('pickupPhoto'+i,file);});
      try{
        var r=await execute(f,flocker);
        if(r.data&&r.data.success){this.pickupModal.show=false;this.pickupModal.record='';this.pickupModal.files=[];await this.loadBooking();this.showToast('Pickup recorded — in transit','success');}
        else this.showToast((r.data&&r.data.message)||'Update failed','error');
      }catch(e){this.showToast('Network error','error')}
      this.pickupModal.busy=false;
    },
    async confirmCancel(){
      this.cancelModal.busy=true;
      var ok=await this.updateStatus('cancelled',this.cancelModal.reason.trim());
      this.cancelModal.busy=false;
      if(ok)this.cancelModal.show=false;
    },
    updateStatusWithGps(status){
      var self=this;
      if(!navigator.geolocation){self.updateStatus(status);return}
      var actorUserId=sessionStorage.getItem('userId')||'';
      navigator.geolocation.getCurrentPosition(
        async function(pos){
          try{
            var r=await executeJson({function:'updateTransportStatus',bookingId:self.booking.id,status:status,actorUserId:actorUserId,currentLatitude:pos.coords.latitude,currentLongitude:pos.coords.longitude},flocker);
            if(r.data&&r.data.success){await self.loadBooking();self.showToast('Status updated','success');}
            else self.showToast((r.data&&r.data.message)||'Update failed','error');
          }catch(e){self.showToast('Network error','error')}
        },
        function(){self.updateStatus(status)},{enableHighAccuracy:true,timeout:10000}
      );
    }
  }
};
</script>
